import {
	ChangeDetectionStrategy,
	Component,
	computed,
	Input,
	Signal,
	signal,
	WritableSignal,
	inject
} from '@angular/core';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Nullable } from '@app/core/models/general.model';
import {
	ExplanationParam,
	ExplanationText,
	SendingExplanation
} from '@ui/lib/shared-modules/modals/alert-modal/alert-sending-explanation/alert-sending-explanation.helper';

interface ResolvedSendingExplanation {
	first: Nullable<string>;
	edges: Nullable<string[]>;
	recurring: Nullable<string>;
}

@Component({
	selector: 'alert-sending-explanation',
	standalone: true,
	imports: [TranslateModule],
	templateUrl: './alert-sending-explanation.component.html',
	styleUrls: ['./alert-sending-explanation.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class AlertSendingExplanationComponent {
	private translateService = inject(TranslateService);

	@Input() set explanation(explanation: Nullable<SendingExplanation>) {
		this.explanationState.set(explanation);
	}

	public readonly I18N_KEY: string = 'MODALS.ALERT.SENDING_EXPLANATION';

	private readonly explanationState: WritableSignal<Nullable<SendingExplanation>> =
		signal<Nullable<SendingExplanation>>(null);

	public readonly resolved: Signal<Nullable<ResolvedSendingExplanation>> = computed(() => {
		const explanation: Nullable<SendingExplanation> = this.explanationState();
		return explanation ? this.resolveExplanation(explanation) : null;
	});

	private resolveExplanation(explanation: SendingExplanation): ResolvedSendingExplanation {
		return {
			first: explanation.first ? this.toSentence(explanation.first) : null,
			edges: explanation.edges ? explanation.edges.map((edge: ExplanationText) => this.toSentence(edge)) : null,
			recurring: explanation.recurring
				? explanation.recurring.map((part: ExplanationText) => this.toSentence(part)).join(' ')
				: null
		};
	}

	// Nested texts (e.g. {when}, {every}) are translated first and passed as plain params
	private resolveText(text: ExplanationText): string {
		const params: Record<string, string | number> = {};
		Object.entries(text.params ?? {}).forEach(([name, value]: [string, ExplanationParam]) => {
			params[name] = typeof value === 'object' ? this.resolveText(value) : value;
		});
		return this.translateService.instant(text.key, params);
	}

	// Sentences may start with a lower-case phrase param (EN "every 3 days, ...")
	private toSentence(text: ExplanationText): string {
		const sentence: string = this.resolveText(text);
		return sentence.charAt(0).toUpperCase() + sentence.slice(1);
	}
}
